import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  useResponsiveValue,
  IconDelete,
  Inline,
  Text,
  Avatar,
  Badge,
  ButtonIcon
} from '@machobear/design-system'
import formatName from '@/utils/format-name'
import { getImageUrl } from '@/utils/image'
import isVerified from '@/utils/is-verified'
import useCompany from '@/hooks/use-company'

import VerifiedBadge from '@/components/verified-badge'
import { Landlord } from '../../types'

const LandlordCard = ({
  landlord,
  isPrimary,
  handleRemove
}: {
  landlord: Landlord
  isPrimary?: boolean
  handleRemove: () => void
}) => {
  const { t } = useTranslation()
  const company = useCompany(landlord.company_id)
  const formattedName = formatName(landlord)

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  return (
    <Box
      display="flex"
      gap="small"
      justifyContent="spaceBetween"
      alignItems="center"
      padding="small"
      borderRadius="small"
      border="borderNeutralLight"
      background="white"
      role="listitem"
      aria-label={formattedName}
    >
      <Inline space="small" alignY="center">
        <Avatar
          size="xsmall"
          shape="circle"
          name={formattedName}
          img={getImageUrl(landlord.avatar?.aws_s3_key, {
            width: 64,
            height: 64
          })}
        />
        <Inline space="none" alignY="center">
          <Text>{formattedName}</Text>
          <VerifiedBadge
            type="id"
            width={30}
            height={22}
            bottom={-2}
            left={2}
            verified={isVerified(landlord, company)}
          />
        </Inline>
        {isPrimary && <Badge tone="info">{t('Primary')}</Badge>}
      </Inline>
      <Inline space="none" alignY="center">
        {isMobile ? (
          <ButtonIcon
            id="remove-landlord"
            tone="critical"
            icon={<IconDelete />}
            variant="transparent"
            onClick={handleRemove}
            label={t('common.remove', 'Remove')}
            size="large"
          />
        ) : (
          <Button
            tone="critical"
            size="small"
            icon={<IconDelete />}
            variant="transparent"
            onClick={handleRemove}
          >
            {t('common.remove', 'Remove')}
          </Button>
        )}
      </Inline>
    </Box>
  )
}

export default LandlordCard
